
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import api from "../../../api/Api.js";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Swal from "sweetalert2";

const AddStatePage = () => {
  const navigate = useNavigate();

  // =========================
  // STATE LIST
  // =========================
  const [getState, setState] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [saving, setSaving] = useState(false);

  // =========================
  // CITY ROWS
  // =========================
  const [stateRows, setStateRows] = useState([
    {
      state_id: "",
      city: "",
      slug: "",
    },
  ]);

  // =========================
  // FETCH STATE DATA
  // =========================
  const fetchStateData = async () => {
    try {
      setLoadingStates(true);

      const res = await api.get(
        "/adminHolidays/get-holidays-state-data"
      );

      const stateData = res.data?.data;

      setState(
        Array.isArray(stateData) ? stateData : []
      );
    } catch (error) {
      console.error("Fetch State Error =>", error);

      toast.error("Failed to load State Name data");
      setState([]);
    } finally {
      setLoadingStates(false);
    }
  };

  useEffect(() => {
    fetchStateData();
  }, []);

  // =========================
  // ADD ROW
  // =========================
  const addStateRow = () => {
    setStateRows((prevRows) => [
      ...prevRows,
      {
        state_id: "",
        city: "",
        slug: "",
      },
    ]);
  };

  // =========================
  // REMOVE ROW
  // =========================
  const removeStateRow = async (index) => {
    if (stateRows.length === 1) {
      toast.error("At least one row is required");
      return;
    }

    const confirm = await Swal.fire({
      title: "Delete Row?",
      text: "Are you sure you want to remove this row?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
    });

    if (!confirm.isConfirmed) return;

    setStateRows((prevRows) =>
      prevRows.filter((_, i) => i !== index)
    );
  };

  // =========================
  // HANDLE INPUT CHANGE
  // =========================
  const handleStateChange = (index, field, value) => {
    setStateRows((prevRows) =>
      prevRows.map((row, i) => {
        if (i !== index) return row;

        const updatedRow = {
          ...row,
          [field]: value,
        };

        // AUTO GENERATE SLUG
        if (field === "city") {
          updatedRow.slug = value
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, "")
            .replace(/\s+/g, "-")
            .replace(/-+/g, "-");
        }

        return updatedRow;
      })
    );
  };

  // =========================
  // VALIDATE CITY ROWS
  // =========================
  const validateStateRows = () => {
    if (stateRows.length === 0) {
      toast.error("Please add at least one city");
      return false;
    }

    for (let i = 0; i < stateRows.length; i++) {
      const row = stateRows[i];

      if (!row.state_id) {
        toast.error(`Please select a state in row ${i + 1}`);
        return false;
      }

      if (!row.city || !row.city.trim()) {
        toast.error(`City name is required in row ${i + 1}`);
        return false;
      }

      if (!row.slug || !row.slug.trim()) {
        toast.error(`Slug is required in row ${i + 1}`);
        return false;
      }
    }

    return true;
  };

  // =========================
  // SAVE CITIES
  // =========================
  const saveState = async () => {
    if (saving) return;

    if (!validateStateRows()) return;

    const confirm = await Swal.fire({
      title: "Save City Names?",
      text: "Do you want to save these City Names?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Save",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0d6efd",
    });

    if (!confirm.isConfirmed) return;

    try {
      setSaving(true);

      // SEND STATE ID WITH EACH CITY
      const payload = {
        states: stateRows.map((row) => ({
          state_id: row.state_id,
          city: row.city.trim(),
          slug: row.slug.trim(),
        })),
      };

      console.log("CITY PAYLOAD =>", payload);

      const res = await api.post(
        "/adminHolidays/holidays-city-save",
        payload
      );

      console.log("CITY SAVE RESPONSE =>", res.data);

      if (res.data?.success) {
        await Swal.fire({
          title: "Success",
          text: "City Names Saved Successfully",
          icon: "success",
          confirmButtonText: "OK",
        });

        // RESET FORM
        setStateRows([
          {
            state_id: "",
            city: "",
            slug: "",
          },
        ]);

        navigate("/holidays/city/manage-city");
      } else {
        await Swal.fire({
          title: "Error",
          text: res.data?.message || "Unable to save City Names",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Save City Error =>", error);
      console.error("Server Response =>", error?.response?.data);

      toast.error(
        error?.response?.data?.message ||
          "Server Error while saving City Names"
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // JSX
  // =========================
  return (
    <div className="container-fluid py-3 bg-white min-vh-100">
      {/* HEADER */}
      <div className="card shadow-sm border-0 mb-4 bg-primary-subtle rounded-3">
        <div className="card-body d-flex justify-content-between align-items-center">
          <h4 className="fw-bold mb-0">Add City Name</h4>

          <Link
            to="/holidays/city/manage-city"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          >
            <ArrowLeft size={16} />
            Back
          </Link>
        </div>
      </div>

      {/* FORM TABLE */}
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "70px" }} className="text-center">
                    Sl.No
                  </th>

                  <th>State Name</th>

                  <th>City Name</th>

                  <th>Slug</th>

                  <th style={{ width: "140px" }} className="text-center">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {stateRows.map((row, index) => (
                  <tr key={index}>
                    {/* SERIAL NUMBER */}
                    <td className="text-center fw-semibold">
                      {index + 1}
                    </td>

                    {/* STATE DROPDOWN */}
                    <td>
                      <select
                        className="form-select"
                        value={row.state_id}
                        disabled={loadingStates}
                        onChange={(e) =>
                          handleStateChange(
                            index,
                            "state_id",
                            e.target.value
                          )
                        }
                      >
                        <option value="">
                          {loadingStates
                            ? "Loading states..."
                            : "- Select State -"}
                        </option>

                        {getState.map((cat) => (
                          <option
                            key={cat.state_id}
                            value={cat.state_id}
                          >
                            {cat.state}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* CITY NAME */}
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter City Name"
                        value={row.city}
                        onChange={(e) =>
                          handleStateChange(
                            index,
                            "city",
                            e.target.value
                          )
                        }
                      />
                    </td>

                    {/* SLUG */}
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Slug"
                        value={row.slug}
                        onChange={(e) =>
                          handleStateChange(
                            index,
                            "slug",
                            e.target.value
                          )
                        }
                      />
                    </td>

                    {/* ACTION BUTTONS */}
                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-success btn-sm me-2"
                        onClick={addStateRow}
                        title="Add Row"
                      >
                        <i className="fa fa-plus-square"></i>
                      </button>

                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => removeStateRow(index)}
                        title="Delete Row"
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* SAVE BUTTON */}
          <div className="text-center mt-4">
            <button
              type="button"
              className="btn btn-info text-white px-5"
              onClick={saveState}
              disabled={saving || loadingStates}
            >
              {saving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa fa-save me-2"></i>
                  Save City Name
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStatePage;
